/**
 * Pipeline distribution bar segments (Workflow page, PR-20).
 *
 * The bar is a proportional view of the v2 summary's stage entries — and
 * ONLY the stage entries. Each board record increments exactly one stage
 * count server-side (server/workflows/routes.ts), so the stage entries are
 * a partition of the caseload: segment counts sum to the true total and
 * every record is represented exactly once.
 *
 * The summary ALSO carries a closure breakdown ({ SD, SC, SP }) used by
 * the Closure card under the rail. That breakdown counts only closed
 * records whose closureType is exactly SD/SC/SP, so it UNDERCOUNTS closed
 * records with no (or a legacy) closure type. It must never be added on
 * top of the stage entries: the 'closed' stage entry already counts every
 * closed record, and appending the breakdown as an extra "Closure"
 * segment double-counts the typed ones (inflating the denominator and
 * rendering two segments labelled "Closure").
 */
import type { StageSummary } from './types';

/**
 * Single-hue ramp for the distribution bar, by stage index. Eight entries
 * for the eight canonical stages — the last is the Closure tone (dark
 * slate), so the closed stage never falls back to a mid-ramp blue.
 */
export const PIPELINE_STAGE_RAMP = [
  'bg-slate-300',
  'bg-slate-400',
  'bg-blue-300',
  'bg-blue-400',
  'bg-blue-500',
  'bg-blue-600',
  'bg-blue-700',
  'bg-slate-800',
];

export interface PipelineSegment {
  /** Stage key — unique per segment, used as the React key. */
  key: string;
  label: string;
  count: number;
  tone: string;
}

/**
 * Build the bar's segments from the summary's stage entries, in stage
 * order. Zero-count stages are omitted (a stage with nobody in it takes
 * no width); tones are assigned by full stage index BEFORE filtering so a
 * stage keeps its color regardless of which neighbours are empty.
 *
 * `total` is the sum over ALL stage entries (zero-count entries add 0),
 * i.e. the whole mapped caseload — the denominator for segment widths.
 * When total <= 0 the caller renders no bar.
 */
export function buildPipelineSegments(
  stages: Pick<StageSummary, 'key' | 'label' | 'count'>[],
): { segments: PipelineSegment[]; total: number } {
  const total = stages.reduce((acc, s) => acc + (s.count || 0), 0);
  const segments = stages
    .map((s, i) => ({
      key: String(s.key),
      label: s.label,
      count: s.count || 0,
      tone: PIPELINE_STAGE_RAMP[i] || 'bg-blue-500',
    }))
    .filter((s) => s.count > 0);
  return { segments, total };
}
